'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** House research-motion easing (citation-map / SignalChart / CeilingStrip). */
export const GROW_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** Per-row entrance offset so bars cascade rather than growing in unison. */
export const GROW_STAGGER_STEP_MS = 70;
/** Bar-width grow duration (matches the citation-map bar transition). */
export const GROW_DURATION_MS = 280;

export interface GrowOnView<T extends HTMLElement> {
  /** Attach to the chart's outermost element. */
  readonly ref: React.RefObject<T | null>;
  /** True once motion is enabled (never under reduced-motion / no observer). */
  readonly armed: boolean;
  /** True once the chart has scrolled into view and bars should be at target. */
  readonly grown: boolean;
}

/**
 * The shared armed/grown scroll-into-view trigger behind every bar chart on
 * this page (HAL-187) – the same single-shot IntersectionObserver contract as
 * `SignalChart` / `CeilingStrip` on the sibling deep-dive:
 *
 * - Until the hook arms (which never happens under prefers-reduced-motion or
 *   when IntersectionObserver is missing) charts render at their final,
 *   fully-visible state, so server-rendered / no-JS / reduced-motion output is
 *   complete.
 * - Once armed, bars collapse to 0 and grow to target the first time the
 *   chart enters the viewport, then the observer disconnects (single-shot).
 */
export function useGrowOnView<T extends HTMLElement>(threshold = 0.25): GrowOnView<T> {
  const ref = useRef<T | null>(null);
  const [armed, setArmed] = useState(false);
  const [grown, setGrown] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const motionPreference =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : undefined;
    const prefersReduced = motionPreference?.matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      // Leave the chart at its final, fully visible state.
      return;
    }

    setArmed(true);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setGrown(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold }
    );
    observer.observe(node);
    const onMotionChange = (event: MediaQueryListEvent): void => {
      if (!event.matches) return;
      observer.disconnect();
      setArmed(false);
      setGrown(true);
    };
    motionPreference?.addEventListener?.('change', onMotionChange);
    return () => {
      observer.disconnect();
      motionPreference?.removeEventListener?.('change', onMotionChange);
    };
  }, [threshold]);

  return { ref, armed, grown };
}
